import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import {
  manageCriticalFlowRecording,
  matchCriticalFlow,
  safeStopCriticalFlowRecording,
} from 'utils/posthogHelpers';

/**
 * Hook to automatically manage PostHog session recording for critical user flows.
 *
 * Critical flows include:
 * - Login/signup flows
 * - Investment flows
 * - Search flows
 * - Partner flows
 * - Account verification flows
 *
 * Usage:
 * ```jsx
 * function App() {
 *   useCriticalFlowRecording();
 *   return <YourApp />;
 * }
 * ```
 *
 * The hook automatically:
 * - Starts recording when entering a critical flow
 * - Stops recording when leaving a critical flow
 * - Tracks flow transitions for analytics
 *
 * @returns {object} Object containing current flow info
 */
const useCriticalFlowRecording = () => {
  const pathname = usePathname();
  const prevPathnameRef = useRef(null);

  useEffect(() => {
    manageCriticalFlowRecording(pathname, prevPathnameRef.current);
    prevPathnameRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    return () => {
      safeStopCriticalFlowRecording();
    };
  }, []);

  // Return current flow info for components that need it
  return {
    currentFlow: matchCriticalFlow(pathname),
    isRecording: !!matchCriticalFlow(pathname),
    pathname,
  };
};

export default useCriticalFlowRecording;
